/**
 * ArcGIS Pro–style Location + Spectral panels for the Raster ToolCard.
 *
 * Driven by live hover samples in `rasterToolCard.lastSample` (written by
 * `PixelInspector` on mousemove). The Spectral chart Y-axis uses band
 * min/max so a low pixel draws a short bar and a high pixel fills toward
 * the top — matching the Explore tool behaviour operators know from Pro.
 */

import { useEffect, useMemo, useState } from 'react';
import { Chip, tokens } from '@/design';
import { useViewerSlice } from '@/state/useViewerSlice';
import { useViewerStore } from '@/state/useViewerStore';
import {
  extractCogQuery,
  fetchTitilerRasterProfile,
  type TitilerRasterProfile,
} from '@/utils/titilerRasterProfile';
import { parseBandStats } from './HistogramPanel';
import {
  formatDecimalLonLat,
  formatGsdPair,
  formatImagePixel,
  formatPedigree,
  formatSpectralAxisTick,
  lonLatToImagePixel,
  resolveSpectralRange,
  spectralBarFraction,
} from './rasterSpectralLocation';

export interface RasterSpectralProfileProps {
  /** TiTiler XYZ template for the active raster (used for /cog/info + stats). */
  tilesUrl?: string | null;
  /** Layer CRS fallback when TiTiler does not report one. */
  layerCrs?: string | null;
  /**
   * When false the panel stays idle (no /cog/info or statistics) — used so
   * collapsing the section stops network work.
   */
  enabled?: boolean;
  'data-testid'?: string;
}

function LocationRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(5.5rem, 38%) 1fr',
        gap: tokens.space[2],
        alignItems: 'baseline',
      }}
    >
      <span
        style={{
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.engMeta.size,
        }}
      >
        {label}
      </span>
      <span
        data-testid={testId}
        style={{
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMono.size,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SpectralChart({
  values,
  bandNames,
  min,
  max,
}: {
  values: readonly number[];
  bandNames: readonly string[];
  min: number;
  max: number;
}) {
  const range = resolveSpectralRange(values, min, max);
  const width = 260;
  const height = 140;
  const padL = 40;
  const padR = 12;
  const padT = 10;
  const padB = 28;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const mid = (range.min + range.max) / 2;
  const n = Math.max(values.length, 1);
  const gap = 8;
  const barW = Math.max(12, (plotW - gap * (n - 1)) / n);

  return (
    <svg
      data-testid="raster-spectral-chart"
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label="Spectral profile"
      style={{ display: 'block' }}
    >
      {/* Plot frame */}
      <rect
        x={padL}
        y={padT}
        width={plotW}
        height={plotH}
        fill={tokens.color.surface[1]}
        stroke={tokens.color.surface[3]}
        strokeWidth={1}
      />
      {/* Horizontal guides */}
      {[0, 0.5, 1].map((t) => {
        const y = padT + plotH * (1 - t);
        return (
          <line
            key={t}
            x1={padL}
            x2={padL + plotW}
            y1={y}
            y2={y}
            stroke={tokens.color.surface[3]}
            strokeWidth={1}
            strokeDasharray={t === 0 || t === 1 ? undefined : '3 3'}
          />
        );
      })}
      {/* Y ticks */}
      <text
        x={padL - 6}
        y={padT + 4}
        textAnchor="end"
        fill={tokens.color.ink.secondary}
        fontFamily={tokens.font.family.mono}
        fontSize={10}
      >
        {formatSpectralAxisTick(range.max)}
      </text>
      <text
        x={padL - 6}
        y={padT + plotH / 2 + 3}
        textAnchor="end"
        fill={tokens.color.ink.secondary}
        fontFamily={tokens.font.family.mono}
        fontSize={10}
      >
        {formatSpectralAxisTick(mid)}
      </text>
      <text
        x={padL - 6}
        y={padT + plotH}
        textAnchor="end"
        fill={tokens.color.ink.secondary}
        fontFamily={tokens.font.family.mono}
        fontSize={10}
      >
        {formatSpectralAxisTick(range.min)}
      </text>
      {/* Bars */}
      {values.map((value, i) => {
        const frac = spectralBarFraction(value, range);
        const h = plotH * frac;
        const x = padL + i * (barW + gap) + (plotW - (n * barW + gap * (n - 1))) / 2;
        const y = padT + plotH - h;
        return (
          <g key={i}>
            <rect
              data-testid={`raster-spectral-bar-${i}`}
              data-fraction={frac.toFixed(4)}
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, value === range.min ? 0 : 1)}
              fill={tokens.color.ink.secondary}
              style={{ transition: 'y 90ms linear, height 90ms linear' }}
            />
            <text
              x={x + barW / 2}
              y={padT + plotH + 14}
              textAnchor="middle"
              fill={tokens.color.ink.secondary}
              fontFamily={tokens.font.family.mono}
              fontSize={10}
            >
              {i + 1}
            </text>
          </g>
        );
      })}
      <text
        x={padL + plotW / 2}
        y={height - 4}
        textAnchor="middle"
        fill={tokens.color.ink.secondary}
        fontFamily={tokens.font.family.ui}
        fontSize={10}
      >
        Bands
      </text>
      {/* Legend under chart is rendered in HTML for wrapping */}
      <title>
        {bandNames.map((name, i) => `${name}: ${values[i]}`).join(', ')}
      </title>
    </svg>
  );
}

export function RasterSpectralProfile({
  tilesUrl,
  layerCrs,
  enabled = true,
  'data-testid': testId,
}: RasterSpectralProfileProps) {
  const sample = useViewerSlice((s) => s.rasterToolCard.lastSample);
  const dataMin = useViewerSlice((s) => s.rasterToolCard.symbologyDataMin);
  const dataMax = useViewerSlice((s) => s.rasterToolCard.symbologyDataMax);
  const stretchMin = useViewerSlice((s) => s.rasterToolCard.stretchMin);
  const stretchMax = useViewerSlice((s) => s.rasterToolCard.stretchMax);
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);

  const [profile, setProfile] = useState<TitilerRasterProfile | null>(null);

  // Load COG header once while the section is open (cached in titilerRasterProfile).
  useEffect(() => {
    if (!enabled || !tilesUrl) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    void fetchTitilerRasterProfile(tilesUrl).then((next) => {
      if (!cancelled) setProfile(next);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, tilesUrl]);

  // Ensure we have a min/max for the Y-axis without waiting for symbology modes.
  useEffect(() => {
    if (!enabled || !tilesUrl) return;
    if (typeof dataMin === 'number' && typeof dataMax === 'number') return;
    const parts = extractCogQuery(tilesUrl);
    if (!parts) return;
    let cancelled = false;
    const url = `${parts.base}/cog/statistics?url=${parts.cogParam}&max_size=128&histogram_bins=8`;
    void fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<Record<string, unknown>>;
      })
      .then((payload) => {
        if (cancelled) return;
        const bands = parseBandStats(payload);
        const band = bands[0];
        if (!band) return;
        setRasterToolCard({
          symbologyDataMin: band.min,
          symbologyDataMax: band.max,
        });
      })
      .catch(() => {
        /* chart falls back to sample-driven range */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, tilesUrl, dataMin, dataMax, setRasterToolCard]);

  const preferredMin = stretchMin ?? dataMin;
  const preferredMax = stretchMax ?? dataMax;

  const imagePixel = useMemo(() => {
    if (!sample || sample.state === 'error') return null;
    return lonLatToImagePixel(sample.lon, sample.lat, profile);
  }, [sample, profile]);

  const decimal = sample
    ? formatDecimalLonLat(sample.lon, sample.lat)
    : '—';
  const pedigree = formatPedigree(profile?.crs, layerCrs);
  const gsd = formatGsdPair(profile);
  const imageXy = formatImagePixel(
    imagePixel?.col ?? null,
    imagePixel?.row ?? null,
  );

  const okValues = sample?.state === 'ok' ? sample.values : [];
  const okNames = sample?.state === 'ok' ? sample.bandNames : [];
  const range = resolveSpectralRange(okValues, preferredMin, preferredMax);

  return (
    <div
      data-testid={testId ?? 'raster-spectral-profile'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[3],
      }}
    >
      <div
        data-testid="raster-spectral-location"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[1],
        }}
      >
        <div
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Location
        </div>
        <LocationRow label="Image (X, Y)" value={imageXy} testId="raster-spectral-image-xy" />
        <LocationRow label="GSD (X, Y)" value={gsd} testId="raster-spectral-gsd" />
        <LocationRow label="Decimal" value={decimal} testId="raster-spectral-decimal" />
        <LocationRow label="Pedigree" value={pedigree} testId="raster-spectral-pedigree" />
      </div>

      <div
        data-testid="raster-spectral-section"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[2],
        }}
      >
        <div
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Spectral
        </div>

        {!sample ? (
          <Chip
            icon="colorize"
            variant="neutral"
            label="Move the cursor over the raster"
          />
        ) : sample.state === 'nodata' ? (
          <Chip icon="block" variant="warning" label="No-data pixel" />
        ) : sample.state === 'error' ? (
          <Chip
            icon="error"
            variant="error"
            label={sample.errorMessage ?? 'Sample failed'}
          />
        ) : (
          <>
            <SpectralChart
              values={okValues}
              bandNames={okNames}
              min={range.min}
              max={range.max}
            />
            <div
              data-testid="raster-spectral-legend"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: tokens.space[2],
                alignItems: 'center',
                fontFamily: tokens.font.family.mono,
                fontSize: tokens.font.role.engMeta.size,
                color: tokens.color.ink.primary,
              }}
            >
              <span style={{ color: tokens.color.ink.secondary }}>
                Bands: {okValues.length}
              </span>
              {okValues.map((value, i) => (
                <span
                  key={i}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      background: tokens.color.ink.secondary,
                      borderRadius: 2,
                    }}
                  />
                  {Number.isInteger(value) ? String(value) : value.toFixed(3)}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
